import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { toFullName } from '@repo/toolkit/to-full-name'
import InnerBox from '@repo/ui/inner-box'
import Loader from '@repo/ui/loader'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'

import { getOtoIndividualLocalRepresentative } from '../../../../api/enrollments/get-oto-individual-local-representative'
import { getOtoIndividualTransferPerson } from '../../../../api/enrollments/get-oto-individual-transfer-person'
import { getOtoIndividualTransferPersonActions } from '../../../../api/enrollments/get-oto-individual-transfer-person-actions'
import { OtoIndividualTransferMethodEnum } from '../../../../lib/types/enums/enrollment/oto-individual-transfer-method.enum'
import { formatPhoneNumber } from '../../../../utils/functions/format-phone-number'

const EMPTY_VALUE = '-'

type LabeledValueProps = {
  label: string
  value?: string
}

// "Nom : Dupont" — the label stays small and grey, the value carries the weight
const LabeledValue = ({ label, value }: LabeledValueProps) => (
  <div className="flex items-baseline gap-1">
    <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
      {`${label} :`}
    </Typography>
    <Typography variant="tag" color={tailwindColors.black.DEFAULT}>
      {value || EMPTY_VALUE}
    </Typography>
  </div>
)

type ContactCardProps = {
  title: string
  introduction?: string
  name?: string
  phoneNumber?: string
  isPending?: boolean
  isError?: boolean
}

const ContactCard = ({
  title,
  introduction,
  name,
  phoneNumber,
  isPending,
  isError
}: ContactCardProps) => (
  <InnerBox className="rounded-lg p-4 mobile:p-3 bg-white-hover gap-3">
    <Typography variant="body-medium" color={tailwindColors.black.DEFAULT}>
      {title}
    </Typography>
    {introduction && (
      <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
        {introduction}
      </Typography>
    )}
    {isPending ? (
      <Loader center />
    ) : isError ? (
      <Typography variant="tag" color={tailwindColors.secondaryRed.DEFAULT}>
        Ces coordonnées n’ont pas pu être chargées. Veuillez réessayer plus tard.
      </Typography>
    ) : (
      <div className="flex flex-col gap-2">
        <LabeledValue label="Nom" value={name} />
        <LabeledValue
          label="Numéro de téléphone"
          value={phoneNumber ? formatPhoneNumber(phoneNumber) : undefined}
        />
      </div>
    )}
  </InnerBox>
)

type Props = {
  enrollmentId: number
  transferMethod?: OtoIndividualTransferMethodEnum
}

// The contacts a parent of a One to One Individuel youth needs on site: the manager of the stay
// and, when the youth is picked up at the airport, the person doing the accueil-transfert.
const ClientOtoIndividualOnSiteContacts = ({ enrollmentId, transferMethod }: Props) => {
  const isAirportPickup = transferMethod === OtoIndividualTransferMethodEnum.AIRPORT_PICKUP

  // the responsable du séjour is the local representative recorded on the host family: the
  // backend only sends it once the placement reached the parents
  const {
    data: localRepresentative,
    isPending: isLocalRepresentativePending,
    isError: isLocalRepresentativeError
  } = useQuery({
    queryKey: ['oto-individual-local-representative', enrollmentId],
    queryFn: async () => getOtoIndividualLocalRepresentative(enrollmentId),
    enabled: !!enrollmentId
  })

  // the backend decides when the parents may see the accueil-transfert person: asking for it
  // outside that window is refused, so the actions endpoint is what opens the request
  const {
    data: transferPersonActions,
    isPending: isTransferPersonActionsPending,
    isError: isTransferPersonActionsError
  } = useQuery({
    queryKey: ['oto-individual-transfer-person-actions', enrollmentId],
    queryFn: async () => getOtoIndividualTransferPersonActions(enrollmentId),
    enabled: !!enrollmentId && isAirportPickup
  })

  const {
    data: transferPerson,
    isPending: isTransferPersonPending,
    isError: isTransferPersonError
  } = useQuery({
    queryKey: ['oto-individual-transfer-person', enrollmentId],
    queryFn: async () => getOtoIndividualTransferPerson(enrollmentId),
    enabled: !!enrollmentId && isAirportPickup && !!transferPersonActions?.canView
  })

  // a disabled query stays "pending" for ever, so every flag is read behind the condition that
  // enables its query
  const canViewTransferPerson = !!transferPersonActions?.canView
  const isTransferCardPending =
    isTransferPersonActionsPending || (canViewTransferPerson && isTransferPersonPending)
  const isTransferCardError = isTransferPersonActionsError || isTransferPersonError

  return (
    <InnerBox className="rounded-lg p-4 flex flex-col gap-3">
      <Typography variant="sub-heading" color={tailwindColors.primary.DEFAULT}>
        Coordonnées sur place
      </Typography>
      <ContactCard
        title="Coordonnées du responsable"
        introduction="Veuillez noter les coordonnées du responsable du séjour à contacter en cas d'urgence, uniquement aux dates du séjour de votre enfant (votre enfant doit aussi conserver ces coordonnées sur lui pendant tout le séjour)."
        name={
          localRepresentative
            ? toFullName(localRepresentative.firstName, localRepresentative.lastName)
            : undefined
        }
        phoneNumber={localRepresentative?.phoneNumber}
        isPending={isLocalRepresentativePending}
        isError={isLocalRepresentativeError}
      />
      {/* the backend opens the accueil-transfert person to the parents shortly before the
          departure only: until then the card itself stays hidden */}
      {isAirportPickup &&
        (isTransferCardPending || isTransferCardError || canViewTransferPerson) && (
          <ContactCard
            title="Coordonnées de la personne en charge de l'accueil-transfert"
            name={
              transferPerson
                ? toFullName(transferPerson.firstName, transferPerson.lastName)
                : undefined
            }
            phoneNumber={transferPerson?.phoneNumber}
            isPending={isTransferCardPending}
            isError={isTransferCardError}
          />
        )}
    </InnerBox>
  )
}

export default ClientOtoIndividualOnSiteContacts
